import Dexie, { type EntityTable } from 'dexie'

/**
 * La base local. Es la **única** fuente de la UI operativa (§5.5).
 *
 * Regla dura: ningún componente hace `fetch`. Si un componente necesita datos
 * que no están acá, el que tiene que traerlos es el sincronizador, no el
 * componente. Un `fetch` dentro de un componente significa que se rompió el
 * modelo, y lo que se rompe con él es el offline entero.
 *
 * Dos canales, con prioridades distintas (§5.5):
 *
 *   operativo  agenda, recursos, horarios, servicios, clientes básicos.
 *              Se baja completo al arrancar. Es lo que habilita la app.
 *   clínico    fichas extendidas, notas, adjuntos. Perezoso, por paciente.
 *              La app se considera lista sin él.
 */

export interface FilaBase {
  id: string
  updated_at: string
  deleted_at: string | null
}

export interface RecursoTipoLocal extends FilaBase {
  nombre: string
  es_persona: boolean
  es_espacio: boolean
  seleccionable_por_cliente: boolean
  buffer_min: number
}

export interface RecursoLocal extends FilaBase {
  nombre: string
  recurso_tipo_id: string
  sucursal_id: string | null
  activo: boolean
}

export interface RecursoHorarioLocal extends FilaBase {
  recurso_id: string
  dia_semana: number
  desde: string
  hasta: string
}

export interface RecursoBloqueoLocal extends FilaBase {
  recurso_id: string
  /** Rango concreto `[inicio, fin)`. `recurso_bloqueo.rango` sigue siendo tstzrange. */
  rango: { inicio: string; fin: string }
  motivo: string | null
}

export interface RecursoAfinidadLocal extends FilaBase {
  persona_id: string
  espacio_id: string
  modo: 'fijo' | 'preferido'
  cede_si_ausente: boolean
}

export interface AsignacionEspacioLocal extends FilaBase {
  sucursal_id: string
  persona_id: string
  espacio_id: string
  dia_semana: number
  desde: string
  hasta: string
  vigencia: string
  estado: 'borrador' | 'publicada' | 'cancelada'
  origen: 'manual' | 'generada'
  creado_por: string | null
}

export interface RecursoCapacidadLocal extends FilaBase {
  recurso_id: string
  capacidad: string
}

export interface ServicioLocal extends FilaBase {
  nombre: string
  precio: number
  pack_vertical: string | null
  reservable_online: boolean
  anticipacion_min_h: number | null
  anticipacion_max_d: number | null
  activo: boolean
  especialidad_requerida_id: string | null
}

export interface ServicioTramoLocal extends FilaBase {
  servicio_id: string
  orden: number
  duracion_min: number
}

export interface ServicioTramoReqLocal extends FilaBase {
  servicio_tramo_id: string
  recurso_tipo_id: string
  cantidad: number
  mismo_recurso_que_tramo: number | null
  capacidad_requerida: string | null
}

export interface ServicioTramoPersonaLocal extends FilaBase {
  servicio_tramo_id: string
  persona_id: string
  duracion_min: number | null
  buffer_min: number | null
}

export interface ClienteLocal extends FilaBase {
  nombre: string
  doc: string | null
  telefono: string | null
  fecha_nacimiento?: string | null
  cobertura_id: string | null
}

export interface CitaLocal extends FilaBase {
  cliente_id: string
  estado: string
  origen: string
  notas: string | null
  /** Cuándo llegó el paciente. Ausente en filas guardadas con la versión anterior de Dexie. */
  llegada_at?: string | null
  /**
   * Sólo en el cliente: la cita creada sin conexión que todavía no confirmó el
   * servidor (§5.3). No existe en la base remota.
   */
  tentativa_local?: boolean
}

export interface CitaTramoLocal extends FilaBase {
  cita_id: string
  orden: number
  /** ISO 8601 en UTC. La zona se aplica sólo al presentar (§6). */
  inicio: string
  fin: string
  /** Ausentes en filas creadas con la versión local anterior; null en tramos sin recursos. */
  asignacion_espacio_id?: string | null
  profesional_principal_id?: string | null
}

export interface ReservaRecursoLocal extends FilaBase {
  cita_tramo_id: string
  recurso_id: string
  /**
   * Rango concreto `[inicio, fin)`. Una reserva es una ocupacion real, no una
   * plantilla: el constraint `reserva_sin_solape` es sobre este rango.
   */
  rango: { inicio: string; fin: string }
  activa: boolean
}

export interface CargoLocal extends FilaBase {
  cita_id: string
  servicio_id: string
  pieza_estado_id?: string | null
  recurso_persona_id: string | null
  /** Guaraníes enteros. Nunca decimales: no existe el céntimo de guaraní. */
  monto: number
  moneda: string
}

export interface PagoLocal extends FilaBase {
  cargo_id: string
  monto: number
  medio: string
  pagador_tipo: 'paciente' | 'tercero'
  /** Donde entro la plata. Ausente en pagos guardados antes de T25. */
  destino?: 'caja' | 'profesional' | 'aseguradora'
  fecha: string
  referencia: string | null
}

export interface FlagLocal extends FilaBase {
  flag: string
  valor: string | null
  activo_desde: string
}

export interface EspecialidadLocal extends FilaBase {
  nombre: string
}

export interface PersonaEspecialidadLocal extends FilaBase {
  persona_id: string
  especialidad_id: string
}

/** Una escritura pendiente de subir. El outbox de §5.4. */
export interface MutacionPendiente {
  /** UUIDv7 generado acá. Es la clave de idempotencia del servidor. */
  mutationId: string
  tipo: 'crear_cliente' | 'crear_cita' | 'mover_cita' | 'cambiar_estado' | 'crear_cargo' | 'crear_pago' | 'marcar_llegada'
  cuerpo: unknown
  creadaEn: string
  intentos: number
  /** Cuándo volver a intentar. El backoff vive acá, no en un timer. */
  proximoIntento: string
  ultimoError?: string
}

/** Una mutación que el servidor rechazó por conflicto. Va a la cola de recepción. */
export interface ConflictoLocal {
  mutationId: string
  tipo: string
  cuerpo: unknown
  motivo: string
  mensaje: string
  detectadoEn: string
  resuelto: boolean
}

export interface Meta {
  clave: string
  valor: string
}

export class BaseLocal extends Dexie {
  recurso_tipo!: EntityTable<RecursoTipoLocal, 'id'>
  recurso!: EntityTable<RecursoLocal, 'id'>
  recurso_horario!: EntityTable<RecursoHorarioLocal, 'id'>
  recurso_bloqueo!: EntityTable<RecursoBloqueoLocal, 'id'>
  recurso_afinidad!: EntityTable<RecursoAfinidadLocal, 'id'>
  asignacion_espacio!: EntityTable<AsignacionEspacioLocal, 'id'>
  recurso_capacidad!: EntityTable<RecursoCapacidadLocal, 'id'>
  servicio!: EntityTable<ServicioLocal, 'id'>
  servicio_tramo!: EntityTable<ServicioTramoLocal, 'id'>
  servicio_tramo_req!: EntityTable<ServicioTramoReqLocal, 'id'>
  servicio_tramo_persona!: EntityTable<ServicioTramoPersonaLocal, 'id'>
  cliente!: EntityTable<ClienteLocal, 'id'>
  cita!: EntityTable<CitaLocal, 'id'>
  cita_tramo!: EntityTable<CitaTramoLocal, 'id'>
  reserva_recurso!: EntityTable<ReservaRecursoLocal, 'id'>
  cargo!: EntityTable<CargoLocal, 'id'>
  pago!: EntityTable<PagoLocal, 'id'>
  flag!: EntityTable<FlagLocal, 'id'>
  especialidad!: EntityTable<EspecialidadLocal, 'id'>
  persona_especialidad!: EntityTable<PersonaEspecialidadLocal, 'id'>
  outbox!: EntityTable<MutacionPendiente, 'mutationId'>
  conflictos!: EntityTable<ConflictoLocal, 'mutationId'>
  meta!: EntityTable<Meta, 'clave'>

  constructor(nombre = 'practiclinic') {
    super(nombre)

    this.version(1).stores({
      recurso_tipo: 'id, updated_at',
      recurso: 'id, recurso_tipo_id, updated_at',
      recurso_horario: 'id, recurso_id, dia_semana',
      recurso_bloqueo: 'id, recurso_id',
      recurso_afinidad: 'id, persona_id, espacio_id',
      recurso_capacidad: 'id, recurso_id',
      servicio: 'id, updated_at',
      servicio_tramo: 'id, servicio_id',
      servicio_tramo_req: 'id, servicio_tramo_id',
      cliente: 'id, nombre, telefono, updated_at',
      cita: 'id, cliente_id, estado, updated_at',
      // `inicio` indexado: la consulta más frecuente de toda la app es "las
      // citas de este día", y sin índice recorre la tabla entera en cada
      // repintado del timeline.
      cita_tramo: 'id, cita_id, inicio, updated_at',
      reserva_recurso: 'id, cita_tramo_id, recurso_id, activa',
      // Cargos y pagos van en el canal operativo: §5.2 los marca como
      // escribibles sin conexión, porque son append-only y no necesitan
      // exclusividad. Cobrar no puede quedar esperando a que vuelva la red.
      cargo: 'id, cita_id, updated_at',
      pago: 'id, cargo_id, updated_at',
      flag: 'id, flag',
      outbox: 'mutationId, proximoIntento, creadaEn',
      conflictos: 'mutationId, resuelto, detectadoEn',
      meta: 'clave',
    })

    // T15: una versión nueva preserva bases instaladas y agrega únicamente la
    // planificación operativa profesional ↔ consultorio.
    this.version(2).stores({
      asignacion_espacio: 'id, persona_id, espacio_id, estado, updated_at',
    })
    this.version(3).stores({
      asignacion_espacio: 'id, persona_id, espacio_id, dia_semana, estado, updated_at',
    })
    this.version(4).stores({
      servicio_tramo_persona: 'id, servicio_tramo_id, persona_id',
    })
    this.version(5).stores({
      especialidad: 'id',
      persona_especialidad: 'id, persona_id, especialidad_id',
    })
  }
}

export const db = new BaseLocal()

/** Cuándo fue el último pull exitoso. Es el `desde` del próximo. */
export const CLAVE_ULTIMO_SYNC = 'ultimo_sync'

export async function leerMeta(clave: string): Promise<string | null> {
  const fila = await db.meta.get(clave)
  return fila?.valor ?? null
}

export async function escribirMeta(clave: string, valor: string): Promise<void> {
  await db.meta.put({ clave, valor })
}

/**
 * Borra todo lo local. Se usa al cambiar de tenant o al cerrar sesión: dejar
 * datos de una clínica en el dispositivo de otra es una fuga, aunque sea el
 * mismo navegador.
 *
 * Con `conservarOutbox` se rehace la base **sin perder lo que todavía no subió**:
 * es el caso de "lo local quedó incompleto" (canal nuevo, o inconsistencia que
 * el sync no puede reconciliar), donde el trabajo pendiente sigue siendo válido
 * porque es del mismo tenant.
 *
 * Leer, vaciar y restaurar va **todo en una transacción**, y no es un detalle
 * de estilo: en tres transacciones separadas, una mutación encolada entre la
 * lectura y el vaciado se borra sin haber existido para nadie, y un cierre del
 * navegador entre el vaciado y la restauración se lleva el outbox completo.
 * Cobrar o agendar sin conexión y perderlo por un cambio de versión es
 * exactamente lo que el outbox existe para evitar.
 */
export async function vaciarBaseLocal(
  opciones: { conservarOutbox?: boolean } = {},
): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    const pendientes = opciones.conservarOutbox ? await db.outbox.toArray() : []
    await Promise.all(db.tables.map((t) => t.clear()))
    if (pendientes.length > 0) await db.outbox.bulkAdd(pendientes)
  })
}
